/**
 * Bottom sheet — the 2026-standard container for secondary content
 * (settings, pickers, confirmations, share menus). Before this, every
 * overlay was hand-rolled: backdrop opacity, rounding, safe-area
 * padding, and close affordances all drifted. This centralizes them.
 *
 * Behavior:
 * - Anchored to the bottom on mobile (thumb zone), centered on ≥sm.
 * - Grabber handle + `rounded-t-2xl`, the platform bottom-sheet cue.
 * - Dismisses on backdrop tap and Escape; the panel stops propagation.
 * - Locks body scroll while open and restores it on close/unmount.
 * - Honors the iOS home-indicator inset via env(safe-area-inset-bottom).
 *
 * Controlled: render it always and drive `open`; it portals to <body>
 * and renders nothing when closed. Provide a `title` (becomes the
 * accessible name) or an explicit `label`.
 */
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** Optional header text, shown top-left and used as the dialog's name. */
  title?: ReactNode;
  /** Accessible name when there's no visible `title`. */
  label?: string;
  children: ReactNode;
}

export function Sheet({ open, onClose, title, label, children }: SheetProps) {
  // Escape to close + lock background scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : label}
        className="w-full sm:max-w-lg bg-paper rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Grabber — the bottom-sheet affordance. */}
        <div className="flex justify-center pt-2.5 pb-1">
          <span className="block h-1 w-9 rounded-full bg-line" aria-hidden />
        </div>
        {title && (
          <header className="px-5 pb-2 pt-1">
            <h2 className="font-serif text-xl text-ink">{title}</h2>
          </header>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
